import React from 'react';
import { FaLanguage, FaUsers, FaGamepad, FaChalkboardTeacher, FaCalendarAlt, FaMicrophoneAlt, FaTrophy, FaFacebookF, FaTwitter, FaInstagram, FaYoutube, FaArrowRight, FaStar, FaStarHalfAlt, FaBars, FaTimes, FaGithub } from 'react-icons/fa';
import { Link } from 'react-router-dom';

const teachers = [
  {
    name: 'Mina',
    city: 'Tehran, Iran',
    img: 'https://randomuser.me/api/portraits/women/44.jpg',
    rating: 4.8,
    reviews: 127,
    tags: ['Beginner', 'Intermediate', 'Poetry'],
    desc: 'Specializes in conversational Farsi and Persian literature. Loves making complex grammar concepts easy to understand.',
    color: 'from-purple-500 to-pink-500',
  },
  {
    name: 'Reza',
    city: 'Shiraz, Iran',
    img: 'https://randomuser.me/api/portraits/men/32.jpg',
    rating: 5.0,
    reviews: 89,
    tags: ['Intermediate', 'Advanced', 'Business'],
    desc: 'Focuses on practical Farsi for travel and business. His interactive games make learning vocabulary fun and memorable.',
    color: 'from-blue-500 to-teal-400',
  },
  {
    name: 'Leila',
    city: 'Isfahan, Iran',
    img: 'https://randomuser.me/api/portraits/women/68.jpg',
    rating: 4.2,
    reviews: 64,
    tags: ['Beginner', 'Culture', 'Fun'],
    desc: 'Creates immersive cultural experiences. Learn Farsi through cooking, music, and art in her unique sessions.',
    color: 'from-pink-500 to-red-500',
  },
];

const testimonials = [
  {
    name: 'Sarah K.',
    img: 'https://randomuser.me/api/portraits/women/33.jpg',
    role: 'Beginner Student',
    rating: 5,
    text: 'I was intimidated to learn Farsi at first, but the games and friendly competitions in Mina\'s classes made it so much fun! I can now have basic conversations after just 2 months.'
  },
  {
    name: 'James P.',
    img: 'https://randomuser.me/api/portraits/men/45.jpg',
    role: 'Business Professional',
    rating: 4.5,
    text: 'Reza\'s business Farsi sessions helped me immensely with my work in Iran. The role-playing exercises were particularly useful for real-world situations.'
  },
  {
    name: 'Maria L.',
    img: 'https://randomuser.me/api/portraits/women/62.jpg',
    role: 'Language Enthusiast',
    rating: 5,
    text: 'Leila\'s cultural immersion events are incredible! Learning Farsi through cooking Persian dishes and listening to traditional music makes the language come alive.'
  },
];

const events = [
  {
    level: 'BEGINNER',
    colorClass: 'bg-purple-600',
    title: 'Farsi Alphabet Adventure',
    date: 'Tomorrow, 10:00 AM EST',
    desc: 'Learn the Farsi alphabet through interactive games and storytelling. Perfect for absolute beginners!',
    joined: 3,
    avatars: [
      'https://randomuser.me/api/portraits/women/32.jpg',
      'https://randomuser.me/api/portraits/men/75.jpg',
      'https://randomuser.me/api/portraits/women/63.jpg',
    ],
    badge: 'New!',
    badgeColor: 'bg-purple-800',
    btnColor: 'bg-purple-100 text-purple-700 hover:bg-purple-200',
  },
  {
    level: 'INTERMEDIATE',
    colorClass: 'bg-pink-600',
    title: 'Persian Poetry Night',
    date: 'Wed, 7:00 PM EST',
    desc: 'Explore the beauty of Persian poetry with Hafez and Rumi. Learn to read and appreciate classic verses.',
    joined: 6,
    avatars: [
      'https://randomuser.me/api/portraits/men/42.jpg',
      'https://randomuser.me/api/portraits/women/44.jpg',
      'https://randomuser.me/api/portraits/men/22.jpg',
      'https://randomuser.me/api/portraits/women/68.jpg',
      '',
      '',
    ],
    badge: 'Popular',
    badgeColor: 'bg-pink-800',
    btnColor: 'bg-pink-100 text-pink-700 hover:bg-pink-200',
  },
  {
    level: 'ADVANCED',
    colorClass: 'bg-blue-600',
    title: 'Debate in Farsi',
    date: 'Fri, 3:00 PM EST',
    desc: 'Practice advanced conversation skills by debating contemporary topics in Farsi. Limited to 5 participants.',
    joined: 2,
    avatars: [
      'https://randomuser.me/api/portraits/women/28.jpg',
      'https://randomuser.me/api/portraits/men/33.jpg',
    ],
    badge: 'Small Group',
    badgeColor: 'bg-blue-800',
    btnColor: 'bg-blue-100 text-blue-700 hover:bg-blue-200',
  },
];

const Events: React.FC = () => {
  return (
    <div className="bg-gray-50 min-h-screen" dir="ltr">
      {/* Hero Section */}
      <div className="hero-pattern bg-gradient-to-b from-purple-50 to-white">
        <div className="max-w-7xl mx-auto py-16 px-4 sm:py-24 sm:px-6 lg:px-8">
          <div className="text-center">
            <h1 className="text-4xl tracking-tight font-extrabold text-gray-900 sm:text-5xl md:text-6xl">
              <span className="block">Learn <span style={{ fontFamily: 'Noto Nastaliq Urdu, serif' }}>Farsi</span> the</span>
              <span className="block text-purple-600">Fun Way!</span>
            </h1>
            <p className="mt-3 max-w-md mx-auto text-base text-gray-500 sm:text-lg md:mt-5 md:text-xl md:max-w-3xl">
              Join interactive virtual events with expert teachers and fellow students. Speak, listen, and practice Farsi in engaging sessions designed to make learning enjoyable.
            </p>
            <div className="mt-5 max-w-md mx-auto sm:flex sm:justify-center md:mt-8">
              <div className="rounded-md shadow">
                <a href="#" className="w-full flex items-center justify-center px-8 py-3 border border-transparent text-base font-medium rounded-md text-white bg-purple-600 hover:bg-purple-700 md:py-4 md:text-lg md:px-10 animate-pulse">
                  Join an Event <FaArrowRight className="ml-2" />
                </a>
              </div>
              <div className="mt-3 rounded-md shadow sm:mt-0 sm:ml-3">
                <a href="#" className="w-full flex items-center justify-center px-8 py-3 border border-transparent text-base font-medium rounded-md text-purple-600 bg-white hover:bg-gray-50 md:py-4 md:text-lg md:px-10">
                  Learn More
                </a>
              </div>
            </div>
          </div>
          {/* Demo Class Card */}
          <div className="mt-16 flex justify-center">
            <div className="relative w-full max-w-4xl">
              <div className="absolute -inset-1 bg-gradient-to-r from-purple-400 to-pink-500 rounded-lg blur opacity-25"></div>
              <div className="relative bg-white rounded-lg shadow-xl overflow-hidden">
                <div className="p-4 bg-gray-50 border-b border-gray-200 flex items-center">
                  <div className="flex space-x-2">
                    <div className="w-3 h-3 rounded-full bg-red-500"></div>
                    <div className="w-3 h-3 rounded-full bg-yellow-500"></div>
                    <div className="w-3 h-3 rounded-full bg-green-500"></div>
                  </div>
                  <div className="ml-4 text-sm text-gray-500">Live Farsi Conversation Class</div>
                </div>
                <div className="p-6">
                  <div className="flex flex-col md:flex-row">
                    <div className="md:w-2/3 mb-6 md:mb-0">
                      <div className="bg-purple-100 rounded-lg p-4 h-full">
                        <div className="flex items-center mb-4">
                          <div className="w-10 h-10 rounded-full bg-purple-600 flex items-center justify-center text-white font-bold">T</div>
                          <div className="ml-3">
                            <div className="font-medium">Teacher Mina</div>
                            <div className="text-xs text-gray-500">Native Farsi Speaker</div>
                          </div>
                        </div>
                        <div className="bg-white rounded-lg p-3 shadow-sm">
                          <p className="text-gray-800">سلام! امروز می‌خواهیم درباره غذاهای ایرانی صحبت کنیم. غذای مورد علاقه شما چیست؟</p>
                          <p className="text-sm text-gray-500 mt-1">(Hello! Today we'll talk about Iranian food. What's your favorite food?)</p>
                        </div>
                      </div>
                    </div>
                    <div className="md:w-1/3 md:pl-4">
                      <div className="bg-gray-50 rounded-lg p-4 h-full">
                        <h3 className="font-medium text-gray-900 mb-2">Participants</h3>
                        <div className="space-y-2">
                          <div className="flex items-center">
                            <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center text-blue-800 text-xs">S1</div>
                            <div className="ml-2 text-sm">Sarah (Beginner)</div>
                          </div>
                          <div className="flex items-center">
                            <div className="w-8 h-8 rounded-full bg-green-100 flex items-center justify-center text-green-800 text-xs">J2</div>
                            <div className="ml-2 text-sm">James (Intermediate)</div>
                          </div>
                          <div className="flex items-center">
                            <div className="w-8 h-8 rounded-full bg-yellow-100 flex items-center justify-center text-yellow-800 text-xs">M3</div>
                            <div className="ml-2 text-sm">Maria (Beginner)</div>
                          </div>
                        </div>
                        <button className="mt-4 w-full px-3 py-1 bg-purple-100 text-purple-700 rounded text-sm hover:bg-purple-200">Join Conversation</button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      {/* Features Section */}
      <div id="features" className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="lg:text-center">
            <h2 className="text-base text-purple-600 font-semibold tracking-wide uppercase">Features</h2>
            <p className="mt-2 text-3xl leading-8 font-extrabold tracking-tight text-gray-900 sm:text-4xl">
              Why Learn With FarsiFun?
            </p>
            <p className="mt-4 max-w-2xl text-xl text-gray-500 lg:mx-auto">
              Our platform is designed to make Farsi learning engaging, effective, and fun.
            </p>
          </div>
          <div className="mt-20 grid gap-10 md:grid-cols-2">
            <div className="relative card-hover transition duration-300 ease-in-out flex">
              <div className="flex items-center justify-center h-12 w-12 rounded-md bg-purple-500 text-white">
                <FaUsers className="text-xl" />
              </div>
              <div className="ml-4">
                <h3 className="text-lg leading-6 font-medium text-gray-900">Interactive Group Sessions</h3>
                <p className="mt-2 text-base text-gray-500">
                  Practice speaking with small groups of students at your level. Our events encourage conversation in a supportive environment.
                </p>
              </div>
            </div>
            <div className="relative card-hover transition duration-300 ease-in-out flex">
              <div className="flex items-center justify-center h-12 w-12 rounded-md bg-purple-500 text-white">
                <FaGamepad className="text-xl" />
              </div>
              <div className="ml-4">
                <h3 className="text-lg leading-6 font-medium text-gray-900">Gamified Learning</h3>
                <p className="mt-2 text-base text-gray-500">
                  Earn points, badges, and level up as you participate. Our game elements make learning addictive in the best way!
                </p>
              </div>
            </div>
            <div className="relative card-hover transition duration-300 ease-in-out flex">
              <div className="flex items-center justify-center h-12 w-12 rounded-md bg-purple-500 text-white">
                <FaChalkboardTeacher className="text-xl" />
              </div>
              <div className="ml-4">
                <h3 className="text-lg leading-6 font-medium text-gray-900">Expert Native Teachers</h3>
                <p className="mt-2 text-base text-gray-500">
                  Learn from passionate Farsi speakers who know how to make complex concepts simple and fun to understand.
                </p>
              </div>
            </div>
            <div className="relative card-hover transition duration-300 ease-in-out flex">
              <div className="flex items-center justify-center h-12 w-12 rounded-md bg-purple-500 text-white">
                <FaCalendarAlt className="text-xl" />
              </div>
              <div className="ml-4">
                <h3 className="text-lg leading-6 font-medium text-gray-900">Flexible Scheduling</h3>
                <p className="mt-2 text-base text-gray-500">
                  Events around the clock to fit your schedule. Morning, afternoon, or night - we've got sessions for you.
                </p>
              </div>
            </div>
            <div className="relative card-hover transition duration-300 ease-in-out flex">
              <div className="flex items-center justify-center h-12 w-12 rounded-md bg-purple-500 text-white">
                <FaMicrophoneAlt className="text-xl" />
              </div>
              <div className="ml-4">
                <h3 className="text-lg leading-6 font-medium text-gray-900">Pronunciation Practice</h3>
                <p className="mt-2 text-base text-gray-500">
                  Get instant feedback on your pronunciation with our speech recognition tools and teacher guidance.
                </p>
              </div>
            </div>
            <div className="relative card-hover transition duration-300 ease-in-out flex">
              <div className="flex items-center justify-center h-12 w-12 rounded-md bg-purple-500 text-white">
                <FaTrophy className="text-xl" />
              </div>
              <div className="ml-4">
                <h3 className="text-lg leading-6 font-medium text-gray-900">Progress Tracking</h3>
                <p className="mt-2 text-base text-gray-500">
                  See your improvement over time with detailed reports and personalized recommendations for your next steps.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
      {/* Upcoming Events */}
      <div id="events" className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h2 className="text-3xl font-extrabold text-gray-900 sm:text-4xl">
              Upcoming Events
            </h2>
            <p className="mt-3 max-w-2xl mx-auto text-xl text-gray-500 sm:mt-4">
              Join our interactive Farsi learning sessions
            </p>
          </div>
          <div className="mt-12 grid gap-8 md:grid-cols-2 lg:grid-cols-3">
            {events.map((event, idx) => (
              <div key={idx} className={`bg-white rounded-lg shadow-md overflow-hidden card-hover transition duration-300 ease-in-out`}>
                <div className={`p-4 ${event.colorClass} text-white`}>
                  <div className="flex justify-between items-center">
                    <span className="text-sm font-semibold">{event.level}</span>
                    <span className={`text-xs ${event.badgeColor} px-2 py-1 rounded-full`}>{event.badge}</span>
                  </div>
                  <h3 className="mt-2 text-xl font-bold text-white">{event.title}</h3>
                  <div className="mt-2 flex items-center text-sm">
                    <FaCalendarAlt className="mr-2" />
                    <span>{event.date}</span>
                  </div>
                </div>
                <div className="p-6">
                  <p className="text-gray-600">{event.desc}</p>
                  <div className="mt-4 flex items-center">
                    <div className="flex -space-x-2">
                      {event.avatars.map((avatar, i) =>
                        avatar ? (
                          <img key={i} className="w-8 h-8 rounded-full border-2 border-white" src={avatar} alt="Participant" />
                        ) : (
                          <div key={i} className="w-8 h-8 rounded-full border-2 border-white bg-gray-200 flex items-center justify-center text-xs">+2</div>
                        )
                      )}
                    </div>
                    <span className="ml-2 text-sm text-gray-500">{event.joined} people joined</span>
                  </div>
                  <div className="mt-6">
                    <button className={`w-full px-4 py-2 ${event.btnColor} rounded-md font-medium transition duration-150 ease-in-out`}>
                      Join Event
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
          <div className="mt-10 text-center">
            <button className="px-6 py-3 border border-transparent text-base font-medium rounded-md text-white bg-purple-600 hover:bg-purple-700">
              View All Events <FaArrowRight className="ml-2" />
            </button>
          </div>
        </div>
      </div>
      {/* Teachers Section */}
      <div id="teachers" className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h2 className="text-3xl font-extrabold text-gray-900 sm:text-4xl">
              Meet Our Teachers
            </h2>
            <p className="mt-3 max-w-2xl mx-auto text-xl text-gray-500 sm:mt-4">
              Passionate native speakers ready to guide your Farsi journey
            </p>
          </div>
          <div className="mt-12 grid gap-8 md:grid-cols-2 lg:grid-cols-3">
            {teachers.map((teacher, idx) => (
              <div key={idx} className="bg-gray-50 rounded-lg overflow-hidden shadow-md card-hover transition duration-300 ease-in-out">
                <div className={`relative h-48 bg-gradient-to-r ${teacher.color} flex items-center justify-center`}>
                  <img className="w-32 h-32 rounded-full border-4 border-white object-cover" src={teacher.img} alt={teacher.name} />
                  <div className="absolute bottom-0 left-0 right-0 bg-black bg-opacity-50 text-white text-center py-2">
                    <h3 className="font-bold">{teacher.name}</h3>
                    <p className="text-xs">{teacher.city}</p>
                  </div>
                </div>
                <div className="p-6">
                  <div className="flex items-center">
                    <div className="flex items-center text-yellow-400">
                      {Array.from({ length: Math.floor(teacher.rating) }).map((_, i) => (
                        <FaStar key={i} />
                      ))}
                      {teacher.rating % 1 !== 0 && <FaStarHalfAlt />}
                    </div>
                    <span className="ml-2 text-gray-600 text-sm">{teacher.rating} ({teacher.reviews} reviews)</span>
                  </div>
                  <p className="mt-3 text-gray-600">{teacher.desc}</p>
                  <div className="mt-4">
                    {teacher.tags.map((tag, i) => (
                      <span key={i} className={`inline-block bg-purple-100 text-purple-800 text-xs px-2 py-1 rounded-full mr-1 mb-1`}>{tag}</span>
                    ))}
                  </div>
                  <button className="mt-4 w-full px-4 py-2 bg-white border border-purple-600 text-purple-600 rounded-md font-medium hover:bg-purple-50 transition duration-150 ease-in-out">
                    View Profile
                  </button>
                </div>
              </div>
            ))}
          </div>
          <div className="mt-10 text-center">
            <button className="px-6 py-3 border border-transparent text-base font-medium rounded-md text-white bg-purple-600 hover:bg-purple-700">
              Meet All Teachers <FaArrowRight className="ml-2" />
            </button>
          </div>
        </div>
      </div>
      {/* Testimonials */}
      <div className="py-16 bg-gradient-to-r from-purple-50 to-blue-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h2 className="text-3xl font-extrabold text-gray-900 sm:text-4xl">
              What Our Students Say
            </h2>
            <p className="mt-3 max-w-2xl mx-auto text-xl text-gray-500 sm:mt-4">
              Join thousands of happy Farsi learners
            </p>
          </div>
          <div className="mt-12 grid gap-8 md:grid-cols-2 lg:grid-cols-3">
            {testimonials.map((t, idx) => (
              <div key={idx} className="bg-white p-6 rounded-lg shadow-md card-hover transition duration-300 ease-in-out">
                <div className="flex items-center">
                  <img className="w-12 h-12 rounded-full object-cover" src={t.img} alt={t.name} />
                  <div className="ml-4">
                    <h4 className="font-medium">{t.name}</h4>
                    <div className="flex items-center mt-1">
                      <div className="flex items-center text-yellow-400">
                        {Array.from({ length: Math.floor(t.rating) }).map((_, i) => (
                          <FaStar key={i} />
                        ))}
                        {t.rating % 1 !== 0 && <FaStarHalfAlt />}
                      </div>
                    </div>
                  </div>
                </div>
                <p className="mt-4 text-gray-600 italic">
                  "{t.text}"
                </p>
                <div className="mt-4 pt-4 border-t border-gray-100 flex items-center">
                  <span className="text-sm text-gray-500">{t.role}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
      {/* CTA Section */}
      <div className="bg-purple-700">
        <div className="max-w-7xl mx-auto py-12 px-4 sm:px-6 lg:py-16 lg:px-8 lg:flex lg:items-center lg:justify-between">
          <h2 className="text-3xl font-extrabold tracking-tight text-white sm:text-4xl">
            <span className="block">Ready to start your Farsi journey?</span>
            <span className="block text-purple-200">Join your first event today.</span>
          </h2>
          <div className="mt-8 flex lg:mt-0 lg:flex-shrink-0">
            <div className="inline-flex rounded-md shadow">
              <Link to="/register" className="inline-flex items-center justify-center px-5 py-3 border border-transparent text-base font-medium rounded-md text-purple-600 bg-white hover:bg-purple-50">
                Sign up for free
              </Link>
            </div>
            <div className="ml-3 inline-flex rounded-md shadow">
              <Link to="/about" className="inline-flex items-center justify-center px-5 py-3 border border-transparent text-base font-medium rounded-md text-white bg-purple-600 bg-opacity-60 hover:bg-opacity-70">
                Learn more
              </Link>
            </div>
          </div>
        </div>
      </div>
      {/* Footer */}
      <footer className="bg-gray-900 text-white pt-16 pb-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            <div>
              <div className="flex items-center mb-4">
                <span className="text-2xl font-bold text-indigo-400 persian-font">پیروز</span>
                                  <span className="ml-2 text-xl font-bold text-white">Pirooz</span>
              </div>
              <p className="text-gray-400 mb-4">
                Master Farsi vocabulary with intelligent learning tools and cultural insights.
              </p>
              <div className="flex space-x-4">
                <a href="https://instagram.com/piroozapp" target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-white">
                  <FaInstagram className="text-xl" />
                </a>
                <a href="https://youtube.com/piroozapp" target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-white">
                  <FaYoutube className="text-xl" />
                </a>
                <a href="https://twitter.com/piroozapp" target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-white">
                  <FaTwitter className="text-xl" />
                </a>
              </div>
            </div>
            <div>
              <h4 className="text-sm font-semibold text-gray-300 uppercase tracking-wider mb-4">Learning</h4>
              <ul className="space-y-2 text-sm">
                <li><Link to="/flashcards" className="text-gray-400 hover:text-white">Flashcards</Link></li>
                <li><Link to="/quiz" className="text-gray-400 hover:text-white">Quizzes</Link></li>
                <li><Link to="/pathways" className="text-gray-400 hover:text-white">Pathways</Link></li>
                <li><Link to="/cultural" className="text-gray-400 hover:text-white">Cultural</Link></li>
                <li><Link to="/events" className="text-gray-400 hover:text-white">Events</Link></li>
              </ul>
            </div>
            <div>
              <h4 className="text-sm font-semibold text-gray-300 uppercase tracking-wider mb-4">Teach</h4>
              <ul className="space-y-2 text-sm">
                <li><Link to="/become-teacher" className="text-gray-400 hover:text-white">Become a Teacher</Link></li>
              </ul>
            </div>
            <div>
              <h4 className="text-sm font-semibold text-gray-300 uppercase tracking-wider mb-4">Support</h4>
              <ul className="space-y-2 text-sm">
                <li><Link to="/help" className="text-gray-400 hover:text-white">Help Center</Link></li>
                <li><Link to="/contact-us" className="text-gray-400 hover:text-white">Contact Us</Link></li>
                <li><Link to="/faq" className="text-gray-400 hover:text-white">FAQ</Link></li>
                <li><Link to="/terms" className="text-gray-400 hover:text-white">Terms of Service</Link></li>
              </ul>
              <h4 className="text-sm font-semibold text-gray-300 uppercase tracking-wider mb-4 mt-8">Connect</h4>
              <div className="flex space-x-4">
                <a href="https://twitter.com/piroozapp" target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-white">
                  <FaTwitter className="text-xl" />
                </a>
                <a href="https://github.com/piroozapp" target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-white">
                  <FaGithub className="text-xl" />
                </a>
              </div>
            </div>
          </div>
          <div className="mt-12 pt-8 border-t border-gray-800 flex flex-col md:flex-row justify-between items-center">
            <p className="text-gray-400 text-sm text-center w-full md:w-auto">© 2024 پیروز Pirooz. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Events; 